
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formSchema, FormValues } from "@/schemas/bilanPatrimoineSchema";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import PersonalInfoSection from './PersonalInfoSection';
import ProductCategorySection from './ProductCategorySection';
import FinancialSituationSection from './FinancialSituationSection';
import ObjectivesConsentSection from './ObjectivesConsentSection';

const BilanPatrimoineForm: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "",
      situation: "",
      productCategory: "",
      revenus: "",
      charges: "",
      immobilier: "",
      financier: "",
      objectifs: "",
      consentement: false,
    },
    mode: "onSubmit", // Validate on submit
  });

  const onSubmit = async (data: FormValues) => {
    console.log("Form submission started with valid data:", data);
    setIsSubmitting(true);
    
    // Ensure immobilier and financier fields have string values even if left empty
    const formData = {
      ...data,
      immobilier: data.immobilier || "Non spécifié",
      financier: data.financier || "Non spécifié",
    };
    
    try {
      console.log("Preparing to submit form data to Formspree:", formData);

      const response = await fetch("https://formspree.io/f/xrbpoweq", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          type: "Demande de Bilan Patrimonial"
        }),
      });
      
      console.log("Form submission response received:", response.status);
      
      if (response.ok) {
        console.log("Form submitted successfully, showing toast");
        toast({
          title: "Formulaire envoyé",
          description: "Nous vous contacterons rapidement pour un rendez-vous.",
          variant: "default",
        });
        
        // Reset form
        form.reset();
        
        // Navigate to thank you page
        console.log("Navigating to thank you page");
        navigate('/merci');
      } else {
        const errorText = await response.text();
        console.error("Formspree error:", errorText);
        throw new Error("Erreur lors de l'envoi du formulaire: " + errorText);
      }
    } catch (error) {
      console.error("Form submission exception:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du formulaire. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      console.log("Form submission process completed");
    }
  };

  // Debug log on render
  console.log("Form render state:", { 
    isSubmitting, 
    formState: form.formState,
    errors: form.formState.errors,
  });

  return (
    <Card className="shadow-lg border-patrimoine-gold/20">
      <CardHeader className="bg-gradient-to-r from-patrimoine-dark to-patrimoine-DEFAULT bg-opacity-90 text-white">
        <CardTitle className="text-2xl">Demande de Bilan Patrimonial</CardTitle>
        <CardDescription className="text-white/80">
          Nos conseillers analyseront votre situation et vous contacteront pour un rendez-vous personnalisé.
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-6">
        <Form {...form}>
          <form 
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <PersonalInfoSection form={form} />
            <ProductCategorySection form={form} />
            <FinancialSituationSection form={form} />
            <ObjectivesConsentSection form={form} />

            <div className="flex items-center justify-between space-x-2">
              <div className="text-sm text-muted-foreground flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                <span>Toutes vos données sont sécurisées et confidentielles</span>
              </div>
              <Button
                type="submit"
                className="cta-button bg-patrimoine-DEFAULT hover:bg-patrimoine-dark"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Envoi en cours..." : "Demander mon bilan"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>

      <CardFooter className="flex flex-col bg-gray-50 border-t border-gray-100 text-sm text-muted-foreground">
        <p className="text-center">
          Nos conseillers vous recontacteront sous 48h pour étudier votre situation ensemble.
        </p>
      </CardFooter>
    </Card>
  );
};

export default BilanPatrimoineForm;
